import RegisterForm from "components/subscription/registerForm";
import dayjs from "dayjs";
import { Tab, TabList, TabPanel, Tabs } from "react-tabs";
import styled from "styled-components";
import { useUser } from "../../context/user";
import { useTab } from "../../hooks/useTab";
import { http } from "../../http";
import { isDev } from "../../lib/config";
import { FullPublicUser } from "../../types";
import Button from "../general/buttonv2";
import { AffiliateConfigured, AffiliateUnconfigured } from "./affiliate";

const StyledTabList = styled(TabList)`
  display: flex;
  list-style-type: none;
  padding: 0;
  margin: 0 0 15px 0;
  > li {
    padding: 10px;
    font-size: 13px;
    background-color: var(--buddy-color);
    border-bottom: 3px solid var(--background-color);
    transition: border-color 0.3s;
  }
  > li[aria-selected="true"] {
    border-bottom: 3px solid #7551cb;
  }
  > li:hover {
    border-bottom: 3px solid #7551cb;
    cursor: pointer;
  }
  > li:focus {
    outline: none;
  }
  > li:not(:nth-of-type(1)) {
    margin: 0 0 0 10px;
  }
`;

export const AccountPage = ({ user }: { user: FullPublicUser }) => {
  const handleLogoutClick = async () => {
    const body: { redirectOrigin?: string } = {};
    if (process.env.NEXT_PUBLIC_DEV_BRAZIL_REDIRECT_ORIGIN && isDev()) {
      body["redirectOrigin"] = process.env.NEXT_PUBLIC_DEV_BRAZIL_REDIRECT_ORIGIN;
    }
    const res = await http.request({
      method: "post",
      url: "/api/checkout/create-portal-session",
      headers: {
        "Content-Type": "application/json",
      },
      data: JSON.stringify(body),
    });

    if (res?.data?.data?.portalSessionUrl) {
      window.location.href = res?.data?.data?.portalSessionUrl;
    }
  };

  return !user.subscription?.isActive ? (
    <RegisterForm />
  ) : (
    <>
      <div
        style={{
          background: "var(--raised-background-color)",
          padding: "1rem 1rem 2rem 1rem",
          borderRadius: "5px",
          marginBottom: "2rem",
          borderLeft: "5px solid #2DD068",
        }}
      >
        <h1>Redact Premium Subscription</h1>
        {user.subscription.label ? (
          <div>
            Plan: <b>{user.subscription.label}</b>
          </div>
        ) : null}
        <div>
          Payment Provider: <b>{user.subscription.type === "PAYPAL" ? "PayPal" : "Stripe"}</b>
        </div>
        {user.subscription.endsAt ? (
          <div>
            Next Billing Date: <b>{dayjs(user.subscription.endsAt)?.format("LLL")}</b>
          </div>
        ) : (
          <div>
            Next Billing Date: <b>Never!</b>
          </div>
        )}
        {user.subscription.type === "PAYPAL" && (
          <div>
            <p>
              Manage your PayPal subscription on your PayPal account, or contact us on our Discord
              for support.
            </p>
          </div>
        )}
      </div>
      {user.subscription.type === "STRIPE" && (
        <Button
          data-action-name="Subscription: Manage Subscription"
          onClick={() => handleLogoutClick()}
          buttonPrimary={true}
        >
          Manage subscription
        </Button>
      )}
    </>
  );
};

const tabs = {
  subscription: 0,
  affiliate: 1,
};

export const LoggedInContent = () => {
  const { userState, affiliateState, createCustomCode, createDefaultCode, updatePaypalEmail } =
    useUser();

  const { selectedTab, onSelectTab } = useTab(tabs, "subscription");

  if (!userState.user) {
    return null;
  }

  return (
    <Tabs selectedIndex={selectedTab} onSelect={onSelectTab}>
      <StyledTabList>
        <Tab key="subscription">Subscription</Tab>
        <Tab>Affiliate</Tab>
      </StyledTabList>
      <TabPanel>
        <AccountPage user={userState.user} />
      </TabPanel>
      <TabPanel>
        {affiliateState?.data?.isConfigured ? (
          <AffiliateConfigured
            createCustomCodeError={affiliateState.setCodeError}
            dashboardData={affiliateState.data}
            loading={affiliateState.loading}
            updatePaypalEmail={updatePaypalEmail}
            createCode={createCustomCode}
          />
        ) : (
          <AffiliateUnconfigured createDefaultCode={createDefaultCode} />
        )}
      </TabPanel>
    </Tabs>
  );
};
